/**
 * Snake Snacks navigation: Shopify preview-bar offset, Categories screen, desktop category tree.
 */

/* ───────── Shopify's preview bar ─────────
   On theme previews Shopify pins its own bar to the bottom of the screen, on top of ours.
   Measure it so the tab bar, add-to-cart bar and messages sit above it. It never exists on
   the live store, where this stays 0. */
const syncPreviewBar = (final = false) => {
  const html = document.documentElement;
  const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
  // the bar can arrive after this runs: keep the height carried over from the last page until we're sure
  if (!bar && !final && html.style.getPropertyValue('--snk-pbar-h')) return;
  let h = 0;
  if (bar) {
    const r = bar.getBoundingClientRect();
    if (getComputedStyle(bar).position === 'fixed' && r.height > 0 && r.bottom >= window.innerHeight - 2) h = r.height;
  }
  h = Math.round(h);
  html.style.setProperty('--snk-pbar-h', `${h}px`);
  try {
    if (h) sessionStorage.setItem('snk-pbar-h', String(h));
    else sessionStorage.removeItem('snk-pbar-h');
  } catch (e) {}
};
syncPreviewBar();
window.addEventListener('resize', () => syncPreviewBar(), { passive: true });
// the bar is injected after the page loads, and can be collapsed
new MutationObserver(() => syncPreviewBar()).observe(document.body, { childList: true });
window.setTimeout(() => syncPreviewBar(), 1500);
window.setTimeout(() => syncPreviewBar(true), 4000);

/* ───────── Categories screen ───────── */

const sheet = /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-snk-categories]'));
if (sheet) {
  const panel = /** @type {HTMLElement | null} */ (sheet.querySelector('[data-cats-panel]'));
  const seg = /** @type {HTMLElement | null} */ (sheet.querySelector('[data-cats-seg]'));
  const thumb = /** @type {HTMLElement | null} */ (sheet.querySelector('[data-cats-thumb]'));
  const tabs = /** @type {HTMLElement[]} */ (Array.from(sheet.querySelectorAll('[data-cats-tab]')));
  const panes = /** @type {HTMLElement[]} */ (Array.from(sheet.querySelectorAll('[data-cats-pane]')));

  // slide the white pill under the selected tab (it can only be measured while the screen is open)
  const moveThumb = (animate = true) => {
    const tab = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (!seg || !thumb || !tab || !tab.offsetWidth) return;
    seg.classList.add('has-thumb');
    if (!animate) seg.classList.add('no-anim');
    thumb.style.width = `${tab.offsetWidth}px`;
    thumb.style.transform = `translateX(${tab.offsetLeft}px)`;
    if (!animate) {
      void thumb.offsetWidth;
      seg.classList.remove('no-anim');
    }
    // keep the selected tab in view if the tabs ever need to scroll sideways
    if (seg.scrollWidth > seg.clientWidth) {
      seg.scrollTo({ left: tab.offsetLeft - (seg.clientWidth - tab.offsetWidth) / 2, behavior: animate ? 'smooth' : 'auto' });
    }
  };

  const select = (key, focus = false) => {
    tabs.forEach((t) => {
      const on = t.dataset.catsTab === key;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      if (on && focus) t.focus();
    });
    panes.forEach((p) => (p.hidden = p.dataset.catsPane !== key));
    const pane = panes.find((p) => !p.hidden);
    if (pane) pane.scrollTop = 0;
    moveThumb();
  };
  const initial = tabs.find((t) => t.getAttribute('aria-selected') === 'true' && panes.some((p) => p.dataset.catsPane === t.dataset.catsTab)) || tabs[0];
  if (initial) select(initial.dataset.catsTab || '');

  tabs.forEach((t, i) => {
    t.addEventListener('click', () => {
      if (t.getAttribute('aria-selected') !== 'true') select(t.dataset.catsTab || '');
    });
    t.addEventListener('keydown', (e) => {
      const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!d) return;
      e.preventDefault();
      const next = tabs[(i + d + tabs.length) % tabs.length];
      select(next.dataset.catsTab || '', true);
    });
  });

  const open = () => {
    sheet.showModal();
    document.documentElement.classList.add('snk-lock');
    // start on the screen itself rather than the close button, so Safari doesn't ring the ✕
    panel?.focus({ preventScroll: true });
    moveThumb(false);
  };
  // page scrolling comes back as soon as the screen is gone (the close event can lag on hidden tabs)
  const unlock = () => document.documentElement.classList.remove('snk-lock');
  // slide out, then close (skipped for reduced motion)
  const close = () => {
    if (!sheet.open || sheet.classList.contains('is-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sheet.close();
      unlock();
      return;
    }
    sheet.classList.add('is-closing');
    // the tiles animate too, so only the screen's own slide-out counts
    const onEnd = (/** @type {AnimationEvent} */ e) => {
      if (e.target === sheet) done();
    };
    const done = () => {
      sheet.removeEventListener('animationend', onEnd);
      if (!sheet.classList.contains('is-closing')) return;
      sheet.classList.remove('is-closing');
      sheet.close();
      unlock();
    };
    sheet.addEventListener('animationend', onEnd);
    window.setTimeout(done, 400);
  };
  sheet.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  sheet.addEventListener('close', unlock);
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });
  sheet.querySelector('[data-close-categories]')?.addEventListener('click', close);
  document.addEventListener('click', (e) => {
    const trigger = /** @type {HTMLElement} */ (e.target).closest('[data-open-categories]');
    if (!trigger) return;
    e.preventDefault();
    // the home page's Snacks / Drinks / Off Menu buttons open straight onto their own tab
    const key = /** @type {HTMLElement} */ (trigger).dataset.openCategories;
    if (key && tabs.some((t) => t.dataset.catsTab === key)) select(key);
    open();
  });
  window.addEventListener('resize', () => sheet.open && moveThumb(false), { passive: true });
}

/* ───────── desktop category tree: arrows expand/collapse, names navigate ───────── */

document.querySelectorAll('[data-snk-tree]').forEach((tree) => {
  tree.addEventListener('click', (e) => {
    const toggle = /** @type {HTMLElement} */ (e.target).closest('[data-tree-toggle]');
    if (toggle) {
      const item = toggle.closest('[data-tree-item]');
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      const list = item?.querySelector(':scope > [data-tree-children]');
      if (list) /** @type {HTMLElement} */ (list).hidden = !open;
      return;
    }
    const all = /** @type {HTMLElement} */ (e.target).closest('[data-tree-all]');
    if (all) {
      const open = all.getAttribute('data-tree-all') === 'expand';
      tree.querySelectorAll('[data-tree-toggle]').forEach((t) => {
        t.setAttribute('aria-expanded', String(open));
        const list = t.closest('[data-tree-item]')?.querySelector(':scope > [data-tree-children]');
        if (list) /** @type {HTMLElement} */ (list).hidden = !open;
      });
    }
  });
});


/* ───────── header category strip: mix the subcategories, then glide them in a loop ───────── */

document.querySelectorAll('[data-marquee]').forEach((box) => {
  const track = /** @type {HTMLElement | null} */ (box.querySelector('[data-marquee-track]'));
  if (!track) return;
  const items = Array.from(track.children);
  // shuffle so snacks and drinks are mixed, a different order on each visit
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  items.forEach((li) => track.append(li));
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // a second, hidden copy makes the loop seamless (the animation slides exactly one copy's width)
  items.forEach((li) => {
    const copy = /** @type {HTMLElement} */ (li.cloneNode(true));
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    track.append(copy);
  });
  const setSpeed = () => {
    const half = track.scrollWidth / 2;
    box.style.setProperty('--snk-marquee-s', `${Math.max(20, half / 40)}s`); // about 40px per second
    box.classList.toggle('is-running', half > box.clientWidth);
  };
  setSpeed();
  window.addEventListener('resize', setSpeed, { passive: true });
  if (window.ResizeObserver) new ResizeObserver(setSpeed).observe(track);
  // touch: hold to pause
  box.addEventListener('touchstart', () => box.classList.add('is-paused'), { passive: true });
  box.addEventListener('touchend', () => setTimeout(() => box.classList.remove('is-paused'), 1500), { passive: true });
});
